// valet-keychain-qr — qr-label-pdf: configurable page size / cols x rows /
// label size / margins form. Clamping (minimum scannable label floor) is
// applied on every change so the preview and export always see a safe config.

import { useTranslation } from '@jq4race/i18n';
import { Input, Label } from '@jq4race/ui';
import { useState } from 'react';

import {
  clampLabelGridConfig,
  type LabelGridConfig,
  MIN_LABEL_SIZE_MM,
  type PageSizeId,
} from '../pdf/label-grid';

export interface LabelConfigFormProps {
  config: LabelGridConfig;
  onChange: (config: LabelGridConfig) => void;
}

function toPositiveInt(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function toPositiveNumber(raw: string, fallback: number): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function LabelConfigForm({ config, onChange }: LabelConfigFormProps) {
  const { t } = useTranslation('qr-generator');
  const [clamped, setClamped] = useState(false);

  function update(patch: Partial<LabelGridConfig>) {
    const raw = { ...config, ...patch };
    const result = clampLabelGridConfig(raw);
    setClamped(result.clamped);
    onChange(result.config);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-[14px] font-medium text-jq-charcoal">{t('labelConfig.heading')}</h3>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div>
          <Label htmlFor="qr-pageSize" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.pageSizeLabel')}
          </Label>
          <select
            id="qr-pageSize"
            value={config.pageSize}
            onChange={(e) => update({ pageSize: e.target.value as PageSizeId })}
            className="flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal"
          >
            <option value="A4">{t('labelConfig.pageSizeA4')}</option>
            <option value="LETTER">{t('labelConfig.pageSizeLetter')}</option>
          </select>
        </div>
        <div>
          <Label htmlFor="qr-columns" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.columnsLabel')}
          </Label>
          <Input
            id="qr-columns"
            type="number"
            min={1}
            max={10}
            value={config.columns}
            onChange={(e) => update({ columns: toPositiveInt(e.target.value, config.columns) })}
          />
        </div>
        <div>
          <Label htmlFor="qr-rows" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.rowsLabel')}
          </Label>
          <Input
            id="qr-rows"
            type="number"
            min={1}
            max={15}
            value={config.rows}
            onChange={(e) => update({ rows: toPositiveInt(e.target.value, config.rows) })}
          />
        </div>
        <div>
          <Label htmlFor="qr-labelWidthMm" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.labelWidthLabel')}
          </Label>
          <Input
            id="qr-labelWidthMm"
            type="number"
            min={MIN_LABEL_SIZE_MM}
            value={config.labelWidthMm}
            onChange={(e) =>
              update({ labelWidthMm: toPositiveNumber(e.target.value, config.labelWidthMm) })
            }
          />
        </div>
        <div>
          <Label htmlFor="qr-labelHeightMm" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.labelHeightLabel')}
          </Label>
          <Input
            id="qr-labelHeightMm"
            type="number"
            min={MIN_LABEL_SIZE_MM}
            value={config.labelHeightMm}
            onChange={(e) =>
              update({ labelHeightMm: toPositiveNumber(e.target.value, config.labelHeightMm) })
            }
          />
        </div>
        <div>
          <Label htmlFor="qr-marginMm" className="mb-1.5 block text-[12px] font-medium">
            {t('labelConfig.marginLabel')}
          </Label>
          <Input
            id="qr-marginMm"
            type="number"
            min={0}
            value={config.marginMm}
            onChange={(e) =>
              update({ marginMm: toPositiveNumber(e.target.value, config.marginMm) })
            }
          />
        </div>
      </div>
      {clamped && (
        <p role="status" className="text-[12px] text-jq-warning">
          {t('labelConfig.clampedWarning', { minMm: MIN_LABEL_SIZE_MM })}
        </p>
      )}
    </div>
  );
}
